import { forwardRef, InputHTMLAttributes } from 'react';
import { clsx } from 'clsx';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ 
    label, 
    error, 
    helperText, 
    className, 
    startIcon, 
    endIcon, 
    disabled,
    ...props 
  }, ref) => {
    const inputClasses = clsx(
      'form-input',
      startIcon && 'pl-10',
      endIcon && 'pr-10',
      error && 'border-red-500 focus:border-red-500 focus:ring-red-500',
      disabled && 'bg-rgf-gray-50 text-rgf-gray-500 cursor-not-allowed',
      className
    );

    return (
      <div className="w-full">
        {label && (
          <label className="form-label">
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          {startIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-rgf-gray-400 h-5 w-5">
                {startIcon}
              </span>
            </div>
          )}
          
          <input
            ref={ref}
            className={inputClasses}
            disabled={disabled}
            {...props}
          />
          
          {endIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className="text-rgf-gray-400 h-5 w-5">
                {endIcon}
              </span>
            </div>
          )}
        </div>
        
        {error && (
          <p className="form-error">{error}</p>
        )}
        
        {helperText && !error && (
          <p className="mt-1 text-sm text-rgf-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';